'use client'

import { CalendarDays, Check, ChevronDown, Clock3, Minus, Plus, Users } from 'lucide-react'
import { useState } from 'react'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

type ReservationDatePickerProps = {
  value: string
  onChange: (value: string) => void
  name?: string
  label?: string
  min?: string
  className?: string
  calendarClassName?: string
}

export function ReservationDatePicker({
  value,
  onChange,
  name,
  label = 'Date',
  min = todayDate(),
  className,
  calendarClassName,
}: ReservationDatePickerProps) {
  const [open, setOpen] = useState(false)
  const selected = fromLocalDate(value)
  const minimum = fromLocalDate(min)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className={cn('reservation-date-trigger', className)} type="button" aria-label={`${label}: ${formatDate(value)}`}>
          <CalendarDays aria-hidden="true" />
          <span><small>{label}</small><strong>{formatDate(value)}</strong></span>
          <ChevronDown className="reservation-control-chevron" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent className={cn('reservation-date-popover', calendarClassName)} align="start">
        <Calendar
          mode="single"
          selected={selected}
          disabled={{ before: minimum }}
          onSelect={(date) => {
            if (!date) return
            onChange(toLocalDate(date))
            setOpen(false)
          }}
        />
      </PopoverContent>
      {name ? <input type="hidden" name={name} value={value} /> : null}
    </Popover>
  )
}

type GuestStepperProps = {
  value: number
  onChange: (value: number) => void
  name?: string
  min?: number
  max?: number
  label?: string
  className?: string
}

export function GuestStepper({
  value,
  onChange,
  name,
  min = 1,
  max = 12,
  label = 'Guests',
  className,
}: GuestStepperProps) {
  const clamped = Math.min(max, Math.max(min, value))

  return (
    <div className={cn('guest-stepper-control', className)}>
      <Users aria-hidden="true" />
      <span aria-live="polite"><small>{label}</small><strong>{clamped} {clamped === 1 ? 'guest' : 'guests'}</strong></span>
      <div className="guest-stepper-actions">
        <button type="button" aria-label="Remove guest" disabled={clamped <= min} onClick={() => onChange(clamped - 1)}><Minus /></button>
        <button type="button" aria-label="Add guest" disabled={clamped >= max} onClick={() => onChange(clamped + 1)}><Plus /></button>
      </div>
      {name ? <input type="hidden" name={name} value={clamped} /> : null}
    </div>
  )
}

type ReservationTimePickerProps = {
  value: string
  onChange: (value: string) => void
  name?: string
  label?: string
  className?: string
  includeAnyTime?: boolean
}

const reservationTimes = Array.from({ length: 49 }, (_, index) => {
  const totalMinutes = 11 * 60 + index * 15
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  const value = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
  return { value, label: formatClock(value) }
})

export function ReservationTimePicker({ value, onChange, name, label = 'Time', className, includeAnyTime = false }: ReservationTimePickerProps) {
  const [open, setOpen] = useState(false)
  const options = includeAnyTime ? [{ value: '', label: 'Any time' }, ...reservationTimes] : reservationTimes
  const selectedLabel = options.find((option) => option.value === value)?.label ?? formatClock(value)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className={cn('reservation-time-trigger', className)} type="button" aria-label={`${label}: ${selectedLabel}`}>
          <Clock3 aria-hidden="true" />
          <span><small>{label}</small><strong>{selectedLabel}</strong></span>
          <ChevronDown className="reservation-control-chevron" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="reservation-time-popover" align="start">
        <div className="reservation-time-options" role="listbox" aria-label="Reservation time">
          {options.map((option) => (
            <button key={option.value || 'any'} type="button" role="option" aria-selected={option.value === value} onClick={() => { onChange(option.value); setOpen(false) }}>
              <span>{option.label}</span>{option.value === value ? <Check aria-hidden="true" /> : null}
            </button>
          ))}
        </div>
      </PopoverContent>
      {name ? <input type="hidden" name={name} value={value} /> : null}
    </Popover>
  )
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
    .format(new Date(`${value}T12:00:00Z`))
}

function formatClock(value: string) {
  const [hours, minutes] = value.split(':').map(Number)
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' })
    .format(new Date(Date.UTC(2026, 0, 1, hours, minutes)))
}

function fromLocalDate(value: string) {
  const [year, month, day] = value.split('-').map(Number)
  return new Date(year, month - 1, day)
}

function todayDate() {
  return toLocalDate(new Date())
}

function toLocalDate(date: Date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}
